import type {
  AvailabilitySlot,
  PublicBookingForm,
  PublicBookingPage,
  PublicBookingResult,
  PublicClinicPage,
  PublicConfirmRequest,
  PublicHoldResult,
  PublicInvitationPrefill,
  PublicPartyConfirmRequest,
  PublicTextReminderWording,
  WaitlistEntryPublic,
  WaitlistJoinRequest,
  WaitlistJoinResult,
  WaitlistOfferAccept,
  WaitlistOfferView,
  CheckInArrival,
  CheckInArriveRequest,
  CheckInFindResult,
  CheckInWalkInRequest,
  ManageArriveRequest,
  PublicCheckInPlace,
  PublicLobbyView,
  PublicQueueView,
} from '@rx-manager/shared';
import type { FormSchema, Lang, Text } from '@rx-manager/forms';

/**
 * The patient site's only channel to the server.
 *
 * Plain `fetch`. There is no token, no session and no cookie: the slug in the
 * path is the entire tenant context, and the server re-scopes every query to it.
 * That is deliberate — a public page that held a credential would be a public
 * page handing one out.
 */

const BASE: string =
  (import.meta.env.VITE_BOOKING_API_URL as string | undefined) ??
  'http://localhost:3010/api';

/**
 * An error carrying the server's own wording.
 *
 * The API answers in sentences a patient can act on — "This service is for
 * patients aged 18 and over", "Someone just took that time" — and throwing them
 * away for a generic "Request failed" is how a booking flow becomes a dead end.
 */
export class BookingApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The server's code, when it sent one: `form_stopped`, `invalid_answers`… */
    readonly code: string | null = null,
    /** What came with the code: `formId`, `errors`, `formIds`, `questionId`. */
    readonly extra: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'BookingApiError';
  }

  /** The slot went while the patient was typing. Worth offering a retry. */
  get isSlotGone(): boolean {
    return this.status === 409 || this.status === 410;
  }

  /** Nothing to show: unknown pharmacy, unpublished page, or a dead link. */
  get isMissing(): boolean {
    return this.status === 404;
  }
}

/** `{ success, data }` on the way out, or `{ success, error }` on the way in. */
interface Envelope<T> {
  success?: boolean;
  data?: T;
  error?: { message?: unknown; statusCode?: number };
}

async function request<T>(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method: init?.method ?? 'GET',
      headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
      body: init?.body ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    // A blocked CORS request and an offline phone are indistinguishable here,
    // and the patient can act on neither — so say the plain, true thing.
    throw new BookingApiError('offline', 0);
  }

  const payload = (await response.json().catch(() => null)) as Envelope<T> | null;

  if (!response.ok) {
    const coded = codedBody(payload);
    throw new BookingApiError(
      messageFrom(payload),
      response.status,
      coded?.code ?? null,
      coded?.extra,
    );
  }
  // Successful bodies are enveloped; the raw value is accepted too so the
  // client does not break if a route is ever mounted without the interceptor.
  return (payload?.data ?? (payload as T));
}

/**
 * Nest nests its own exception body inside the envelope's `error`, and
 * `class-validator` reports an array of failures. Unwrap both, or a patient
 * sees "[object Object]".
 */
function messageFrom(payload: Envelope<unknown> | null): string {
  const raw: unknown =
    payload?.error && typeof payload.error === 'object'
      ? ((payload.error as { message?: unknown }).message ?? payload.error)
      : payload;

  const inner: unknown =
    raw && typeof raw === 'object' && 'message' in raw
      ? (raw).message
      : raw;

  if (typeof inner === 'string') return inner;
  if (Array.isArray(inner) && typeof inner[0] === 'string') return inner[0];
  return 'unknown';
}

/** A refusal with a code (Track SFM: the booking's forms), and what came with it. */
function codedBody(
  payload: Envelope<unknown> | null,
): { code: string; extra: Record<string, unknown> } | null {
  const inner = payload?.error?.message;
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return null;
  const body = inner as Record<string, unknown>;
  if (typeof body.code !== 'string') return null;
  return {
    code: body.code,
    extra: Object.fromEntries(
      Object.entries(body).filter(([name]) => !['code', 'message', 'statusCode'].includes(name)),
    ),
  };
}

const encode = encodeURIComponent;

export const bookingApi = {
  /** The pharmacy, its sites and what it offers. 404 if not published. */
  page: (slug: string): Promise<PublicBookingPage> =>
    request(`/public/booking/${encode(slug)}`),

  /** Open times. Recomputed on every read — never cached by the server. */
  availability: (
    slug: string,
    query: {
      serviceId: string;
      locationId: string;
      from: string;
      to: string;
      /** Track CGS — 2 to 5: times where the whole party's block fits. Sent only for a party. */
      partySize?: number;
    },
  ): Promise<AvailabilitySlot[]> => {
    const { partySize, ...rest } = query;
    const params = new URLSearchParams(rest);
    if (partySize !== undefined && partySize > 1) params.set('partySize', String(partySize));
    return request(`/public/booking/${encode(slug)}/availability?${params}`);
  },

  /**
   * Track CGS (CGS-D3) — a clinic's own page: the booking page's shape with the clinic as its one
   * place. One 404 for a code that is unknown, unpublished or past, as for the page.
   */
  clinicPage: (slug: string, code: string): Promise<PublicClinicPage> =>
    request(`/public/booking/${encode(slug)}/clinics/${encode(code)}`),

  /**
   * Track SFM rev 2 — the forms the booking asks for this service, one question at a time after
   * the time. A 404 is "none": an older server has no such route, and the booking goes on as
   * before. Anything else is an error the page says out loud, never "no forms", or a required
   * form would be skipped only to be refused at Confirm.
   */
  serviceForms: async (slug: string, serviceId: string): Promise<PublicBookingForm[]> => {
    try {
      const result = await request<{ forms: PublicBookingForm[] }>(
        `/public/booking/${encode(slug)}/services/${encode(serviceId)}/forms`,
      );
      return result.forms ?? [];
    } catch (error) {
      if (error instanceof BookingApiError && error.isMissing) return [];
      throw error;
    }
  },

  /**
   * Takes the slot off the market for the length of the form. Track CGS — with `partySize` (2 to
   * 5, sent only for a party), the whole party's block with one pharmacist.
   */
  hold: (
    slug: string,
    body: { serviceId: string; locationId: string; startAt: string; partySize?: number },
  ): Promise<PublicHoldResult> =>
    request(`/public/booking/${encode(slug)}/holds`, { method: 'POST', body }),

  /** Gives it back when the patient steps backwards. */
  releaseHold: (slug: string, holdToken: string): Promise<{ released: boolean }> =>
    request(`/public/booking/${encode(slug)}/holds/${encode(holdToken)}`, {
      method: 'DELETE',
    }),

  confirm: (
    slug: string,
    body: PublicConfirmRequest,
  ): Promise<PublicBookingResult> =>
    request(`/public/booking/${encode(slug)}/appointments`, {
      method: 'POST',
      body,
    }),

  /**
   * Track CGS (CGS-D11 to D13) — books everyone held by the party's token, each as their own
   * patient. A refusal for one person is a 422 whose `extra.person` (1 to 5) names them.
   */
  confirmParty: (slug: string, body: PublicPartyConfirmRequest): Promise<PublicBookingResult> =>
    request(`/public/booking/${encode(slug)}/appointments/party`, {
      method: 'POST',
      body,
    }),

  invitation: (slug: string, token: string): Promise<PublicInvitationPrefill> =>
    request(`/public/booking/${encode(slug)}/invitations/${encode(token)}`),

  managed: (token: string): Promise<PublicBookingResult> =>
    request(`/public/booking/manage/${encode(token)}`),

  reschedule: (token: string, holdToken: string): Promise<PublicBookingResult> =>
    request(`/public/booking/manage/${encode(token)}/reschedule`, {
      method: 'POST',
      body: { holdToken },
    }),

  /**
   * Cancels the visit behind the link. Track CGS — on a party's link, `person` cancels one of
   * them (their own email, the others keep their visits) and `all` everyone.
   */
  cancel: (
    token: string,
    reason?: string,
    target?: { person?: number; all?: boolean },
  ): Promise<PublicBookingResult> =>
    request(`/public/booking/manage/${encode(token)}/cancel`, {
      method: 'POST',
      body: { reason, ...target },
    }),
};

// ── Track BCW: the waitlist and its offers ───────────────────────────────────
//
// Joining is addressed by the slug, like a booking. A place on the waitlist and an
// offer are reached by their link's token, always in the JSON body, never in a
// path or a query string (BCW-D11, D17).

export const waitlistApi = {
  /** Join a service's waitlist (BCW-D9, D10). Joining twice returns the first place. */
  join: (slug: string, body: WaitlistJoinRequest): Promise<WaitlistJoinResult> =>
    request(`/public/booking/${encode(slug)}/waitlist`, { method: 'POST', body }),

  /** The patient's own place (`/w/<token>`). */
  entry: (token: string): Promise<WaitlistEntryPublic> =>
    request('/public/booking/waitlist/view', { method: 'POST', body: { token } }),

  leave: (token: string): Promise<WaitlistEntryPublic> =>
    request('/public/booking/waitlist/leave', { method: 'POST', body: { token } }),

  /** "Keep me on the list", after a pause for lapsed offers. */
  keep: (token: string): Promise<WaitlistEntryPublic> =>
    request('/public/booking/waitlist/keep', { method: 'POST', body: { token } }),

  /** An offer (`/o/<token>`). */
  offer: (token: string): Promise<WaitlistOfferView> =>
    request('/public/booking/offers/view', { method: 'POST', body: { token } }),

  /** Book the offered time. 410 `offer_closed` (with `status`) when it ended first. */
  accept: (token: string, body: WaitlistOfferAccept = {}): Promise<WaitlistOfferView> =>
    request('/public/booking/offers/accept', { method: 'POST', body: { token, ...body } }),

  /** No thanks; `leave` also ends the place. */
  decline: (token: string, leave = false): Promise<WaitlistOfferView> =>
    request('/public/booking/offers/decline', { method: 'POST', body: { token, leave } }),
};

// ── Track CIQ: check-in and the queue ───────────────────────────────────────
//
// A place's check-in page is addressed by its poster's code: it names a place, never a person.
// A surname and a birth date go in the JSON body, and the patient's own place in line and a lobby
// screen by their link's token, in the body too, never in a path or a query string (CIQ-D40).

export const checkInApi = {
  /** A place's check-in page (`/i/<code>`): the brand, the place, walk-ins open now. */
  place: (code: string): Promise<PublicCheckInPlace> =>
    request(`/public/booking/check-in/${encode(code)}`),

  /** Find my visit: today's visits there, by surname and birth date. */
  find: (code: string, body: { lastName: string; birthDate: string }): Promise<CheckInFindResult> =>
    request(`/public/booking/check-in/${encode(code)}/find`, { method: 'POST', body }),

  /** I'm here: the people of a match, into the line. */
  arrive: (code: string, body: CheckInArriveRequest): Promise<CheckInArrival> =>
    request(`/public/booking/check-in/${encode(code)}/arrive`, { method: 'POST', body }),

  /** Join as a walk-in: 409 `walk_ins_closed`, `clinic_full` or `already_waiting`. */
  joinWalkIn: (code: string, body: CheckInWalkInRequest): Promise<CheckInArrival> =>
    request(`/public/booking/check-in/${encode(code)}/walk-in`, { method: 'POST', body }),

  /** The patient's own place in line (`/q/<token>`); `forms` also lists what is still due. */
  queue: (token: string, forms = false): Promise<PublicQueueView> =>
    request('/public/booking/queue/view', { method: 'POST', body: { token, forms } }),

  /** Leave the line. A booked visit is cancelled quietly: no email or text. */
  leave: (token: string): Promise<PublicQueueView> =>
    request('/public/booking/queue/leave', { method: 'POST', body: { token } }),

  /** A lobby screen (`/l/<token>`): ticket numbers only. */
  lobby: (token: string): Promise<PublicLobbyView> =>
    request('/public/booking/lobby/view', { method: 'POST', body: { token } }),

  /** I'm here from a manage link, for an in-person visit today. */
  manageArrive: (manageToken: string, body: ManageArriveRequest): Promise<CheckInArrival> =>
    request(`/public/booking/manage/${encode(manageToken)}/arrive`, { method: 'POST', body }),
};

// ── Track SFM: patient forms ─────────────────────────────────────────────────
//
// Every call is a POST with the token in the JSON body, never in a path or a
// query string, so no request log anywhere holds a form link (SFM-D8). The
// browser keeps only the session secret, in sessionStorage (SFM-D10).

export type FormLinkState =
  | 'verify_dob'
  | 'ready'
  | 'submitted'
  | 'expired'
  | 'locked'
  | 'cancelled'
  | 'not_needed'
  | 'replaced'
  | 'used'
  | 'restart'
  | 'unavailable';

export type FormPharmacy = {
  displayName: string;
  logoUrl: string | null;
  supportPhone: string | null;
};

export type FormSession = {
  secret: string;
  idleUntil: string;
  until: string | null;
};

/** The form, once the link is proven: the patient's view, with any draft. */
export type FormReady = {
  state: 'ready';
  pharmacy: FormPharmacy;
  session: FormSession;
  form: FormSchema;
  version: number;
  languages: Lang[];
  language: Lang | null;
  draft: Record<string, unknown>;
};

/** Anything short of the form: a date of birth to check, or why it cannot open. */
export type FormGate = {
  state: Exclude<FormLinkState, 'ready'>;
  pharmacy: FormPharmacy;
  /** With `verify_dob`: wrong dates of birth left before the link locks. */
  triesLeft?: number;
};

export type FormSent = {
  submitted: true;
  at: string;
  /** The visit's next form, already open for this patient. */
  next: { token: string; session: string } | null;
};

export type PublicFormInfo =
  | {
      state: 'ready';
      pharmacy: FormPharmacy;
      form: { title: Text; intro: Text | null; languages: Lang[] };
      /** Cloudflare Turnstile site key, or null when the pharmacy runs on limits only. */
      spamCheck: string | null;
    }
  | { state: 'unavailable'; pharmacy: FormPharmacy };

export type PublicFormStarted = FormReady & { token: string };

export type PublicIdentityInput = {
  firstName: string;
  lastName: string;
  dob: string;
  phone?: string;
  email?: string;
};

/** A refusal with the server's code and details: `dob_mismatch` with `triesLeft`, and so on. */
export class FormApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'FormApiError';
  }
}

async function formRequest<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/public/forms/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // The page carries the token in its own address: never pass it on.
      referrerPolicy: 'no-referrer',
    });
  } catch {
    throw new FormApiError('offline', 'offline', 0);
  }
  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => null)) as Envelope<T> | null;
  if (!response.ok) {
    const inner = payload?.error?.message;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const body = inner as Record<string, unknown>;
      const extra = Object.fromEntries(
        Object.entries(body).filter(([name]) => !['code', 'message', 'statusCode'].includes(name)),
      );
      throw new FormApiError(
        typeof body.code === 'string' ? body.code : 'unknown',
        typeof body.message === 'string' ? body.message : 'unknown',
        response.status,
        extra,
      );
    }
    throw new FormApiError('unknown', messageFrom(payload), response.status);
  }
  return payload?.data ?? (payload as T);
}

export const formsApi = {
  open: (token: string): Promise<FormReady | FormGate> => formRequest('open', { token }),
  verify: (token: string, dob: string): Promise<FormReady | FormGate> =>
    formRequest('verify', { token, dob }),
  resume: (token: string, session: string): Promise<FormReady | FormGate> =>
    formRequest('resume', { token, session }),
  saveDraft: (input: {
    token: string;
    session: string;
    version: number;
    answers: Record<string, unknown>;
    language: Lang;
  }): Promise<{ savedAt: string; idleUntil: string }> => formRequest('draft', input),
  extend: (token: string, session: string): Promise<{ idleUntil: string }> =>
    formRequest('extend', { token, session }),
  end: (token: string, session: string): Promise<void> => formRequest('end', { token, session }),
  submit: (input: {
    token: string;
    session: string;
    version: number;
    answers: Record<string, unknown>;
    language: Lang;
    nonce: string;
  }): Promise<FormSent> => formRequest('submit', input),
  openPublic: (publicToken: string): Promise<PublicFormInfo> =>
    formRequest('p/open', { publicToken }),
  startPublic: (input: {
    publicToken: string;
    turnstile?: string;
    website?: string;
    identity: PublicIdentityInput;
    language: Lang;
  }): Promise<PublicFormStarted> => formRequest('p/start', input),
};

// ── Track ORX: online refill, renewal and transfer requests ─────────────────
//
// No sign-in: a 6-digit code texted to the patient's mobile proves the number is theirs (ORX-D3).
// Every call after the first is a POST whose values ride in the JSON body, never in a path or a
// query string, so no request log anywhere holds a name, a number, a code or a medication. The
// browser keeps nothing: the challenge and the submit token live in the page's memory only.

/** What a patient can ask for online. */
export type RefillKind = 'refill' | 'renewal' | 'transfer';
export const REFILL_KINDS: readonly RefillKind[] = ['refill', 'renewal', 'transfer'];

/** `GET public/refill/:slug` — whether the pharmacy takes online requests, and how to call it. */
export type RefillPageInfo = {
  available: boolean;
  pharmacyName: string;
  phone?: string | null;
  types: RefillKind[];
  /**
   * Cloudflare Turnstile's site key where the pharmacy has one, as the waitlist sends it. Not in the
   * ORX contract yet: absent means no widget, and the start goes on the server's other limits.
   */
  turnstileSiteKey?: string | null;
  /**
   * The server's versioned words behind "Text me when it's ready" (ORX-D8), as booking sends its
   * reminders' wording. Not in the ORX contract yet: absent means the box shows its label alone.
   */
  textWhenReady?: { wordings: PublicTextReminderWording[] } | null;
  /**
   * ORX-D16 — whether the pharmacy asks for a texted code: `'none'` when its switch
   * (`online_refills_skip_code`) lets the page start without one. An older server sends nothing,
   * which reads as `'code'`: the code flow as before.
   */
  verification?: 'code' | 'none';
};

/** One prescription: its number from the label, or the medication's name. */
export type RefillItemInput = { rx?: string; name?: string };

export type RefillStartInput = {
  firstName: string;
  lastName: string;
  /** `YYYY-MM-DD`. */
  dob: string;
  phone: string;
  /** The page's language: the code text goes out in it, and the consent row records its words. */
  language: Lang;
  /** The hidden field only a bot fills in. Sent only when filled. */
  website?: string;
  turnstileToken?: string;
  /**
   * ORX-D16 — start without a texted code. Sent only when `GET :slug` said `verification: 'none'`;
   * the server still decides, and answers no `submitToken` when it texts a code after all.
   */
  verification?: 'none';
};

/**
 * What the start answers: the challenge, and (ORX-D17) a submit token straight away when the code
 * was skipped. Without one, a code went out and the code step follows, as before.
 */
export type RefillStarted = {
  challengeId: string;
  submitToken?: string;
  /**
   * ORX-D24 — with a submit token: this mobile once texted STOP to the pharmacy, so no "ready"
   * text can reach it. Only `true` counts; absent (an older server, or not stopped) is as before.
   */
  textsStopped?: boolean;
};

export type RefillSubmitInput = {
  submitToken: string;
  kind: RefillKind;
  /** 1 to 10. */
  items: RefillItemInput[];
  prescriber?: string;
  transferFrom?: { name: string; phone?: string };
  /** `YYYY-MM-DD`, today or later. */
  neededBy?: string;
  /** Up to 500 characters. */
  note?: string;
  textWhenReady: boolean;
  /**
   * ORX-D20 — an address for "we got your request" and "ready for pickup", for this request only.
   * Sent only when typed; a bad one is 422 `email_invalid`, with the token kept.
   */
  email?: string;
};

/**
 * A refusal with its reason code (ORX-D12): `not_available`, `too_many`, `invalid`, `wrong_code`
 * (with `triesLeft`), `locked`, `expired`; `missing` for a 404 and `offline` when nothing answered.
 * The start's own checks add `name_missing`, `name_too_long`, `dob_invalid`, `phone_invalid` and
 * `spam_check_failed`; a resend asked too soon is `too_soon` (with `retryAfter`); the submit adds
 * `email_invalid`. A start or resend whose mobile once texted STOP is 409 `texts_stopped` (ORX-D24).
 */
export class RefillApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'RefillApiError';
  }

  /** With `wrong_code`: wrong codes left before the code locks. */
  get triesLeft(): number | null {
    const value = this.extra.triesLeft;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  /**
   * With a 429: how long until another code may be sent. RxManager_BE's `too_soon` and `too_many`
   * carry it as `retryAfter`; `retryAfterSeconds` and the `Retry-After` header are read too.
   */
  get retryAfterSeconds(): number | null {
    for (const raw of [this.extra.retryAfterSeconds, this.extra.retryAfter]) {
      if (raw === undefined || raw === null) continue;
      const value = Number(raw);
      if (Number.isFinite(value) && value > 0) return Math.ceil(value);
    }
    return null;
  }
}

/** The codes a refusal may carry as its whole message, for a server that sends a bare string. */
const REFILL_REASONS: ReadonlySet<string> = new Set([
  'not_available',
  'too_many',
  'invalid',
  'wrong_code',
  'locked',
  'expired',
  // The start's own checks, which the page puts by the field they are about (found 2026-10-28).
  'name_missing',
  'name_too_long',
  'dob_invalid',
  'phone_invalid',
  'spam_check_failed',
  // A resend inside the minute, with `retryAfter`; a code asked for where none is owed (ORX-D17).
  'too_soon',
  'already_verified',
  // ORX-D20 — the submit's email.
  'email_invalid',
  // ORX-D24 — no code can be texted to a mobile that once texted STOP (409, on start and resend).
  'texts_stopped',
]);

/** A refusal's code and what came with it, from the envelope or a bare body. */
function refillRefusal(
  payload: Envelope<unknown> | null,
  status: number,
  retryAfter: string | null,
): RefillApiError {
  const inner: unknown = payload?.error?.message ?? payload?.error ?? payload;
  let code: string | null = null;
  let extra: Record<string, unknown> = {};
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const body = inner as Record<string, unknown>;
    const named = [body.code, body.reason, body.error].find(
      (value): value is string => typeof value === 'string' && REFILL_REASONS.has(value),
    );
    code = named ?? null;
    extra = Object.fromEntries(
      Object.entries(body).filter(
        ([name]) => !['code', 'reason', 'error', 'message', 'statusCode'].includes(name),
      ),
    );
  } else if (typeof inner === 'string' && REFILL_REASONS.has(inner)) {
    code = inner;
  }
  // The body's own wait first (`retryAfter` from RxManager_BE); the header only when it has none.
  if (
    extra.retryAfterSeconds === undefined &&
    extra.retryAfter === undefined &&
    retryAfter &&
    Number(retryAfter) > 0
  ) {
    extra.retryAfterSeconds = Number(retryAfter);
  }
  const fallback =
    status === 429 ? 'too_many' : status === 404 ? 'missing' : status === 400 ? 'invalid' : 'unknown';
  return new RefillApiError(code ?? fallback, status, extra);
}

async function refillRequest<T>(path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/public/refill/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      // Nothing about this page goes on to anyone else.
      referrerPolicy: 'no-referrer',
    });
  } catch {
    throw new RefillApiError('offline', 0);
  }
  const payload = (await response.json().catch(() => null)) as Envelope<T> | null;
  if (!response.ok) {
    throw refillRefusal(payload, response.status, response.headers.get('Retry-After'));
  }
  const data = (payload?.data ?? payload) as unknown;
  // A refusal answered as a 200 (`{ error: 'wrong_code', triesLeft }`) is a refusal all the same.
  if (data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string') {
    throw refillRefusal({ error: { message: data } }, response.status, null);
  }
  return data as T;
}

export const refillApi = {
  /** Whether the pharmacy takes online requests. A 404 for an unknown or unpublished pharmacy. */
  info: (slug: string): Promise<RefillPageInfo> => refillRequest(encode(slug)),
  /**
   * Texts the code, or (ORX-D17) skips it and answers the submit token at once. The same answer
   * whether or not the person is on file (ORX-D5).
   */
  start: (slug: string, input: RefillStartInput): Promise<RefillStarted> =>
    refillRequest(`${encode(slug)}/start`, input),
  /** Another code, 60 seconds after the last; a 429 says how long to wait. */
  resend: (challengeId: string): Promise<{ ok: boolean }> => refillRequest('resend', { challengeId }),
  verify: (challengeId: string, code: string): Promise<{ submitToken: string }> =>
    refillRequest('verify', { challengeId, code }),
  /** One request per token (ORX-D13): a retry answers the same reference. */
  submit: (input: RefillSubmitInput): Promise<{ reference: string }> => refillRequest('submit', input),
};
